import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import type { VsImageProps } from '@vs-shop/headless/useVsImage';
import type { VsLinkProps } from '@vs-shop/headless/vsLink';
import Image from '@vs-shop/image-link/Image';
import Link from '@vs-shop/image-link/Link';
import { clsxm, type ClassValue } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The variant swatch row: the same product in its other finishes, each a
 * thumbnail that navigates to its own page, under a line naming the one being
 * looked at.
 *
 *   <ProductVariantSwatches>
 *     <ProductVariantSwatchesLabel>
 *       <ProductVariantSwatchesTerm>Barva:</ProductVariantSwatchesTerm>
 *       <ProductVariantSwatchesValue>{hovered ?? current}</ProductVariantSwatchesValue>
 *     </ProductVariantSwatchesLabel>
 *
 *     <ProductVariantSwatchesList>
 *       <ProductVariantSwatch variant={ProductVariantSwatchVariant.CURRENT}>
 *         <ProductVariantSwatchLink href={href} aria-label={name}>
 *           <ProductVariantSwatchMedia>
 *             <ProductVariantSwatchImage src={src} alt={name} width={101} height={76} />
 *           </ProductVariantSwatchMedia>
 *         </ProductVariantSwatchLink>
 *       </ProductVariantSwatch>
 *     </ProductVariantSwatchesList>
 *   </ProductVariantSwatches>
 *
 * **Nothing here works out what the variants are called.** A storefront that
 * lists "Kraton modrá" beside "Kraton zelená" wants each swatch labelled
 * "modrá", not the whole name again — which is a diff over sibling names, a
 * tenant's projection over its own catalogue. The parts take a RESOLVED label,
 * a resolved href and a resolved image URL, and paint a row.
 *
 * **The row's state is the tenant's too.** Which swatch is current, and which
 * one a pointer is over, are answers a composition holds; the handlers ride the
 * native prop spread and the label's value is a child. That is why no part in
 * this file carries `'use client'` — a tenant's own client composition holds the
 * state, the same shape the gallery and its lightbox already have.
 */

/**
 * The row.
 *
 * Its own element rather than the list's, because the query context and the
 * list are two jobs: an element cannot query itself, so the swatches' `@[...]`
 * steps need a box outside the one they sit in. A composition omitting it would
 * still render — with every step silently compiling to nothing — which is why
 * the row owns it instead of tenant JSX.
 *
 * No appearance axis: a row of finishes is laid out the same way wherever the
 * panel puts it.
 */
const productVariantSwatchesClasses = '@container';

export type ProductVariantSwatchesProps = ComponentProps<'div'>;

/** The swatch row. */
export const ProductVariantSwatches = ({ className, ...props }: ProductVariantSwatchesProps) => (
  <div
    data-slot="product-variant-swatches"
    className={clsxm(productVariantSwatchesClasses, className)}
    {...props}
  />
);

/**
 * The line above the swatches, naming the finish in view.
 *
 * A neutral element: a section is furniture a page places, and a row of
 * finishes inside a product panel is not the page's subject. What the line SAYS
 * is two parts rather than one string, because only half of it changes as a
 * pointer crosses the row.
 */
const productVariantSwatchesLabelClasses =
  'font-heading text-foreground flex gap-1 text-lg/[18px] whitespace-nowrap';

export type ProductVariantSwatchesLabelProps = ComponentProps<'div'>;

/** The line naming the finish. */
export const ProductVariantSwatchesLabel = ({
  className,
  ...props
}: ProductVariantSwatchesLabelProps) => (
  <div
    data-slot="product-variant-swatches-label"
    className={clsxm(productVariantSwatchesLabelClasses, className)}
    {...props}
  />
);

/** What the line is about — "Barva:", "Farba:", "Colour:". The tenant's word. */
const productVariantSwatchesTermClasses = 'font-semibold';

export type ProductVariantSwatchesTermProps = ComponentProps<'span'>;

/** The label's term. */
export const ProductVariantSwatchesTerm = ({
  className,
  ...props
}: ProductVariantSwatchesTermProps) => (
  <span
    data-slot="product-variant-swatches-term"
    className={clsxm(productVariantSwatchesTermClasses, className)}
    {...props}
  />
);

export type ProductVariantSwatchesValueProps = ComponentProps<'span'>;

/**
 * The finish being named.
 *
 * Carries no recipe: its type and colour come from the line around it, and WHAT
 * it says — the current finish, or the one a pointer is over — is a value the
 * composition holds and hands in as a child. The element exists so the anatomy
 * is reachable, and so the half of the line that changes is the only half that
 * re-renders.
 */
export const ProductVariantSwatchesValue = ({
  className,
  ...props
}: ProductVariantSwatchesValueProps) => (
  <span
    data-slot="product-variant-swatches-value"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/** The swatches themselves, a row that gains air once there is room for it. */
const productVariantSwatchesListClasses = 'flex gap-3 pt-4 @sm:gap-4';

export type ProductVariantSwatchesListProps = ComponentProps<'ul'>;

/** The list the swatches sit in. */
export const ProductVariantSwatchesList = ({
  className,
  ...props
}: ProductVariantSwatchesListProps) => (
  <ul
    data-slot="product-variant-swatches-list"
    className={clsxm(productVariantSwatchesListClasses, className)}
    {...props}
  />
);

/**
 * One finish.
 *
 * The ring is the appearance: the finish in view is ringed in the brand's own
 * colour and twice as thick, the others in a hairline that only separates the
 * thumbnail from the panel behind it.
 */
export const productVariantSwatchVariants = cva('rounded-sm', {
  variants: {
    variant: {
      /** The finish the page is about. */
      current: 'ring-primary ring-2',
      /** A finish to go to. */
      plain: 'ring-foreground/40 ring',
      unstyled: '',
    },
  },
});

type ProductVariantSwatchVariantName = NonNullable<
  VariantProps<typeof productVariantSwatchVariants>['variant']
>;

export type ProductVariantSwatchVariant = ProductVariantSwatchVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductVariantSwatchVariant = {
  CURRENT: 'current',
  PLAIN: 'plain',
  /** The opt-out: a tenant marking the current finish itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductVariantSwatchVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductVariantSwatchVariantProps = {
  readonly variant: ProductVariantSwatchVariant;
};

export type ProductVariantSwatchProps = ComponentProps<'li'> & ProductVariantSwatchVariantProps;

/** One finish in the row. */
export const ProductVariantSwatch = ({
  className,
  variant,
  ...props
}: ProductVariantSwatchProps) => (
  <li
    data-slot="product-variant-swatch"
    className={clsxm(productVariantSwatchVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The whole swatch is the click target.
 *
 * ARIA contract, as ratified for whole-surface links: a swatch wraps a
 * thumbnail and nothing else, so it reaches assistive tech unnamed unless the
 * tenant names it — which it does through `aria-label`, in its own language.
 * There is no second link beside it to be hidden from, so the `aria-hidden`
 * half of the contract does not arise here; a composition that hides one anyway
 * loses the tab stop with it, the same way every other link part behaves.
 *
 * No `variant`: what changes between a current swatch and a sibling is the ring
 * around the box, which {@link ProductVariantSwatch} draws.
 */
const productVariantSwatchLinkClasses = 'tab:gap-4 relative flex flex-col gap-3';

export type ProductVariantSwatchLinkProps = VsLinkProps;

/** The swatch's click surface. */
export const ProductVariantSwatchLink = ({
  className,
  tabIndex,
  ...props
}: ProductVariantSwatchLinkProps) => {
  const isAriaHidden = props['aria-hidden'] === true || props['aria-hidden'] === 'true';
  const resolvedTabIndex = tabIndex ?? (isAriaHidden ? -1 : undefined);
  // `exactOptionalPropertyTypes` rejects an explicit `undefined` here.
  const tabIndexProp = resolvedTabIndex === undefined ? {} : { tabIndex: resolvedTabIndex };

  return (
    <Link
      data-slot="product-variant-swatch-link"
      className={clsxm(productVariantSwatchLinkClasses, className)}
      {...tabIndexProp}
      {...props}
    />
  );
};

/**
 * The box a swatch's picture sits in — a fixed landscape crop that grows one
 * step once the row has room, so a panel narrow enough to stack still shows
 * every finish on one line.
 *
 * Takes children rather than a picture, because "no picture" is a state of its
 * own here: a finish awaiting photography still occupies its place in the row,
 * and what stands in for it is a mark the tenant composes.
 */
const productVariantSwatchMediaClasses =
  'flex aspect-[4/3] h-[64px] w-full items-center justify-center overflow-hidden rounded-sm @md:h-[76px]';

export type ProductVariantSwatchMediaProps = ComponentProps<'div'>;

/** The frame a swatch's picture fills. */
export const ProductVariantSwatchMedia = ({
  className,
  ...props
}: ProductVariantSwatchMediaProps) => (
  <div
    data-slot="product-variant-swatch-media"
    className={clsxm(productVariantSwatchMediaClasses, className)}
    {...props}
  />
);

/**
 * The finish's photograph, cropped rather than contained: these are the same
 * product shot the same way, so filling the frame keeps the row even and the
 * differences between finishes side by side.
 *
 * `src` is final and renderable — CDN/Liip resolution stays app-side — and
 * there is no `sources`, because a swatch framing one crop is not art-directed.
 */
const productVariantSwatchImageClasses = 'h-full w-full object-cover';

export type ProductVariantSwatchImageProps = Omit<VsImageProps, 'className' | 'fill'> & {
  readonly className?: ClassValue;
};

/** The finish's photograph. */
export const ProductVariantSwatchImage = ({
  className,
  ...props
}: ProductVariantSwatchImageProps) => (
  <Image
    data-slot="product-variant-swatch-image"
    className={clsxm(productVariantSwatchImageClasses, className)}
    {...props}
  />
);
